import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  alpha,
  keyframes,
  useTheme,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import DashboardHeader from '../components/common/DashboardHeader';
import { DataTable, FormDatePicker } from '../shared';
import { THEME_COLORS } from '../shared/constants/theme';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import 'moment-timezone';
import {
  deleteDataMigrationHistory,
  fetchDataMigrationHistory,
  fetchDataMigrationHistoryDetail,
  importDataMigrationFile,
  validateDataMigrationFile,
} from '../services/api';
import {
  getConfigurationCardGridSpacing,
  getConfigurationEmbeddedIconBoxSx,
  getConfigurationEmbeddedRootSx,
  getConfigurationEmbeddedSectionHeaderSx,
  getConfigurationEmbeddedSectionLeadSx,
  getConfigurationEmbeddedSectionSubtitleSx,
  getConfigurationEmbeddedSectionTitleSx,
  getConfigurationFinanceCardSx,
  getConfigurationNeutralOutlinedButtonSx,
  getConfigurationTableContainerSx,
  getConfigurationTextFieldSx,
  isConfigurationMobileLayout,
} from '../utils/configurationMobileLayout';
import {
  BOOKING_DRAWER_WIDTH_SX,
  getBookingDrawerFooterSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPaperSx,
  getBookingDrawerPrimaryButtonSx,
} from '../utils/bookingDrawerLayout';

const ACCENT = '#4ce1b6';
const ACCENT_DEEP = '#0d9488';
const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];
const ACCEPT_ATTR = '.csv,.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv';
const MIGRATION_TEMPLATE_URL = '/templates/roomnexa-data-migration-template.xlsx';
const MIGRATION_TEMPLATE_FILENAME = 'Roomnexa_Data_Migration_Template.xlsx';

const STATUS_OPTIONS = ['Success', 'Pending', 'Failed', 'Processing'];

const TEMPLATE_TIPS = [
  'Exact template headers',
  'Room No matches inventory',
  'Dates as YYYY-MM-DD',
  'GST % in Included or Excluded',
  'Numeric payment columns',
  'One stay per row',
];

const softPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(76, 225, 182, 0.35); }
  70% { box-shadow: 0 0 0 12px rgba(76, 225, 182, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 225, 182, 0); }
`;

const driftIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

function fileFormatFromName(name = '') {
  const lower = name.toLowerCase();
  if (lower.endsWith('.csv')) return 'CSV';
  if (lower.endsWith('.xlsx')) return 'XLSX';
  if (lower.endsWith('.xls')) return 'XLS';
  return 'FILE';
}

const MIGRATION_STAY_DATE_FORMAT = 'DD-MM-YYYY';
const MIGRATION_DATETIME_IST_FORMAT = 'DD-MM-YYYY hh:mm A [IST]';

/** Validation stay dates: always DD-MM-YYYY. */
function formatMigrationStayDate(value) {
  if (value == null || value === '') return '—';
  const m = moment(
    value,
    [moment.ISO_8601, 'YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY', 'D/M/YYYY', 'D-M-YYYY'],
    true
  );
  if (!m.isValid()) {
    const loose = moment(value);
    return loose.isValid() ? loose.format(MIGRATION_STAY_DATE_FORMAT) : String(value);
  }
  return m.format(MIGRATION_STAY_DATE_FORMAT);
}

/** Migration history timestamps in IST as DD-MM-YYYY hh:mm AM/PM IST. */
function formatMigrationDateTimeIst(value) {
  if (value == null || value === '') return '—';
  // Already 12-hour display from API (e.g. 28-07-2026 10:49 PM IST)
  if (
    typeof value === 'string'
    && /\d{2}-\d{2}-\d{4}\s+\d{1,2}:\d{2}\s*(AM|PM)\s*IST/i.test(value)
  ) {
    return value.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
  }
  let m;
  if (moment.tz) {
    m = moment.tz(value, 'Asia/Kolkata');
    if (!m.isValid()) {
      m = moment.tz(
        value,
        [
          'YYYY-MM-DD HH:mm',
          'YYYY-MM-DD hh:mm A',
          'YYYY-MM-DDTHH:mm:ssZ',
          'DD-MM-YYYY hh:mm A [IST]',
          'DD-MM-YYYY HH:mm [IST]',
          moment.ISO_8601,
        ],
        'Asia/Kolkata'
      );
    }
  } else {
    m = moment(value);
  }
  if (!m.isValid()) return String(value);
  return m.format(MIGRATION_DATETIME_IST_FORMAT);
}

function formatNowStamp() {
  if (moment.tz) {
    return moment.tz('Asia/Kolkata').format(MIGRATION_DATETIME_IST_FORMAT);
  }
  return moment().format(MIGRATION_DATETIME_IST_FORMAT);
}

/** YYYY-MM-DD calendar key for history date filter (IST). */
function migrationUploadedDateKey(row) {
  const raw = row?.uploadedAtIso || row?.uploadedAt || '';
  if (!raw) return '';
  if (moment.tz) {
    const m = moment.tz(raw, 'Asia/Kolkata');
    if (m.isValid()) return m.format('YYYY-MM-DD');
  }
  const m = moment(
    raw,
    [
      moment.ISO_8601,
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD hh:mm A',
      'DD-MM-YYYY hh:mm A [IST]',
      'DD-MM-YYYY HH:mm [IST]',
      'DD-MM-YYYY hh:mm A',
      'DD-MM-YYYY HH:mm',
    ],
    true
  );
  if (m.isValid()) return m.format('YYYY-MM-DD');
  const loose = moment(raw);
  return loose.isValid() ? loose.format('YYYY-MM-DD') : String(raw).slice(0, 10);
}

/** Mark migrated booking IDs as already-seen so global booking popups do not appear. */
function markMigratedBookingsAsSeen(bookingIds = []) {
  const hotelId = window?.DJANGO_CONTEXT?.selectedHotel?.id ?? 'all';
  const keys = [
    `roomnexa_booking_notifications_seen_${hotelId}`,
    'roomnexa_booking_notifications_seen_all',
  ];
  const incoming = bookingIds
    .map((id) => (id != null ? String(id) : ''))
    .filter(Boolean);
  if (!incoming.length) return;
  keys.forEach((key) => {
    try {
      const raw = localStorage.getItem(key);
      const existing = raw ? JSON.parse(raw) : [];
      const list = Array.isArray(existing) ? existing.map(String) : [];
      incoming.forEach((id) => {
        if (!list.includes(id)) list.push(id);
      });
      localStorage.setItem(key, JSON.stringify(list.slice(-100)));
    } catch {
      // ignore storage errors
    }
  });
}

function statusChipSx(status, isDark) {
  const map = {
    Success: { bg: alpha(ACCENT, isDark ? 0.18 : 0.14), color: isDark ? '#8de5cd' : '#0f766e', border: alpha(ACCENT, 0.45) },
    Pending: { bg: alpha('#f59e0b', isDark ? 0.18 : 0.12), color: isDark ? '#fbbf24' : '#b45309', border: alpha('#f59e0b', 0.45) },
    Failed: { bg: alpha('#ef4444', isDark ? 0.18 : 0.1), color: isDark ? '#fca5a5' : '#b91c1c', border: alpha('#ef4444', 0.4) },
    Processing: { bg: alpha('#38bdf8', isDark ? 0.18 : 0.12), color: isDark ? '#7dd3fc' : '#0369a1', border: alpha('#38bdf8', 0.45) },
  };
  return map[status] || map.Pending;
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function mapHistoryApiRow(row = {}) {
  const uploadedAtIso = row.uploaded_at_iso || row.uploadedAtIso || '';
  const uploadedRaw = uploadedAtIso || row.uploaded_at || row.uploadedAt || '';
  const details = row.details && typeof row.details === 'object' ? { ...row.details } : {};
  const importedBookingIds = Array.isArray(row.imported_booking_ids)
    ? row.imported_booking_ids
    : Array.isArray(details.imported_booking_ids)
      ? details.imported_booking_ids
      : (Array.isArray(details.imported)
          ? details.imported.map((item) => item?.booking_id).filter((id) => id != null && id !== '')
          : []);
  const importedCodes = Array.isArray(row.imported_codes)
    ? row.imported_codes
    : Array.isArray(details.imported_codes)
      ? details.imported_codes
      : (Array.isArray(details.imported)
          ? details.imported.map((item) => item?.code).filter(Boolean)
          : []);
  if (!Array.isArray(details.imported_booking_ids)) {
    details.imported_booking_ids = importedBookingIds;
  }
  if (!Array.isArray(details.imported_codes)) {
    details.imported_codes = importedCodes;
  }
  return {
    id: row.id ?? `local-${Date.now()}`,
    fileName: row.file_name || row.fileName || '',
    fileFormat: row.file_format || row.fileFormat || fileFormatFromName(row.file_name || row.fileName || ''),
    totalRecords: Number(row.total_records ?? row.totalRecords ?? 0),
    importedRecords: Number(row.imported_count ?? row.importedRecords ?? 0),
    failedRecords: Number(row.failed_count ?? row.failedRecords ?? 0),
    uploadedBy: row.uploaded_by || row.uploadedBy || 'User',
    uploadedAtIso,
    uploadedAt: formatMigrationDateTimeIst(uploadedRaw),
    status: row.status || 'Pending',
    message: row.message || '',
    details,
    importedBookingIds,
    importedCodes,
  };
}

const ROOMNEXA_LOGO_URL = '/static/topbar/logo_ed_dark.png';
const PDF_ACCENT_RGB = [76, 225, 182];

async function loadRoomnexaLogoDataUrl() {
  try {
    const response = await fetch(ROOMNEXA_LOGO_URL);
    if (!response.ok) return null;
    const logoBlob = await response.blob();
    const objectUrl = URL.createObjectURL(logoBlob);
    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = objectUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || 300;
      canvas.height = image.naturalHeight || 87;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    return null;
  }
}

async function downloadHistoryReport(row) {
  const details = row.details || {};
  const imported = Array.isArray(details.imported) ? details.imported : [];
  const failed = Array.isArray(details.failed) ? details.failed : [];
  const safeName = String(row.fileName || 'migration').replace(/\.[^.]+$/, '');

  const rows = [
    ...imported.map((item) => ({
      row: item.row_number ?? '',
      guest: item.guest_name || '',
      code: item.code || '',
      booking_id: item.booking_id ?? '',
      status: 'Imported',
      errors: '',
    })),
    ...failed.map((item) => ({
      row: item.row_number ?? '',
      guest: item.guest_name || '',
      code: '',
      booking_id: '',
      status: 'Failed',
      errors: Array.isArray(item.errors) ? item.errors.join('; ') : String(item.errors || ''),
    })),
  ].sort((a, b) => Number(a.row || 0) - Number(b.row || 0));

  if (!rows.length) {
    rows.push({
      row: '—',
      guest: 'No rows in this migration',
      code: '',
      booking_id: '',
      status: '—',
      errors: '',
    });
  }

  const logoDataUrl = await loadRoomnexaLogoDataUrl();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;

  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', (pageWidth - 36) / 2, 8, 36, 10);
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(30, 41, 59);
    doc.text('roomnexa', pageWidth / 2, 14, { align: 'center' });
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(30, 41, 59);
  doc.text('Migration History Report', pageWidth / 2, 24, { align: 'center' });

  // Meta info panel (designed like the history drawer details)
  let y = 28;
  const metaItems = [
    { label: 'File', value: row.fileName || '—' },
    { label: 'Format', value: row.fileFormat || '—' },
    { label: 'Status', value: row.status || '—' },
    { label: 'Uploaded by', value: row.uploadedBy || '—' },
    { label: 'Uploaded at', value: row.uploadedAt || '—' },
    { label: 'Generated', value: formatNowStamp() },
  ];
  const metaCols = 3;
  const metaGap = 3;
  const metaCardW = (pageWidth - margin * 2 - metaGap * (metaCols - 1)) / metaCols;
  const metaCardH = 12;
  metaItems.forEach((item, index) => {
    const col = index % metaCols;
    const rowIdx = Math.floor(index / metaCols);
    const x = margin + col * (metaCardW + metaGap);
    const cardY = y + rowIdx * (metaCardH + metaGap);
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, cardY, metaCardW, metaCardH, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text(String(item.label).toUpperCase(), x + 2.5, cardY + 4);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(30, 41, 59);
    const valueLines = doc.splitTextToSize(String(item.value), metaCardW - 5);
    doc.text(valueLines[0] || '—', x + 2.5, cardY + 8.5);
  });
  y += Math.ceil(metaItems.length / metaCols) * (metaCardH + metaGap) + 2;

  if (row.message) {
    const summaryLines = doc.splitTextToSize(String(row.message), pageWidth - margin * 2 - 8);
    const summaryH = 8 + summaryLines.length * 3.6;
    doc.setFillColor(236, 253, 245);
    doc.setDrawColor(...PDF_ACCENT_RGB);
    doc.setLineWidth(0.4);
    doc.roundedRect(margin, y, pageWidth - margin * 2, summaryH, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(15, 118, 110);
    doc.text('SUMMARY', margin + 3, y + 4);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(51, 65, 85);
    doc.text(summaryLines, margin + 3, y + 8);
    y += summaryH + 4;
  } else {
    y += 2;
  }

  // Summary cards (match Migration History drawer: Total / Imported / Failed)
  const totalCount = Number(row.totalRecords ?? 0);
  const importedCount = Number(row.importedRecords ?? 0);
  const failedCount = Number(row.failedRecords ?? 0);
  const cardGap = 4;
  const cardWidth = (pageWidth - margin * 2 - cardGap * 2) / 3;
  const cardHeight = 18;
  const cards = [
    {
      label: 'TOTAL',
      value: totalCount,
      border: [148, 163, 184],
      valueColor: [30, 41, 59],
      labelColor: [100, 116, 139],
      fill: [248, 250, 252],
    },
    {
      label: 'IMPORTED',
      value: importedCount,
      border: [34, 197, 94],
      valueColor: [22, 163, 74],
      labelColor: [22, 163, 74],
      fill: [240, 253, 244],
    },
    {
      label: 'FAILED',
      value: failedCount,
      border: [239, 68, 68],
      valueColor: [220, 38, 38],
      labelColor: [220, 38, 38],
      fill: [254, 242, 242],
    },
  ];
  cards.forEach((card, index) => {
    const x = margin + index * (cardWidth + cardGap);
    doc.setFillColor(...card.fill);
    doc.setDrawColor(...card.border);
    doc.setLineWidth(0.4);
    doc.roundedRect(x, y, cardWidth, cardHeight, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...card.valueColor);
    doc.text(String(card.value), x + cardWidth / 2, y + 8, { align: 'center' });
    doc.setFontSize(7);
    doc.setTextColor(...card.labelColor);
    doc.text(card.label, x + cardWidth / 2, y + 13.5, { align: 'center' });
  });
  y += cardHeight + 5;

  autoTable(doc, {
    startY: y,
    head: [['Row', 'Guest', 'Booking Code', 'Booking ID', 'Status', 'Errors / Notes']],
    body: rows.map((item) => [
      String(item.row),
      String(item.guest),
      String(item.code),
      String(item.booking_id),
      String(item.status),
      String(item.errors),
    ]),
    styles: { fontSize: 8, cellPadding: 1.4, overflow: 'linebreak' },
    headStyles: { fillColor: PDF_ACCENT_RGB, textColor: 20, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 252, 250] },
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      if (data.section !== 'body' || data.column.index !== 4) return;
      const value = String(data.cell.raw || '').toLowerCase();
      if (value === 'imported') {
        data.cell.styles.textColor = [15, 118, 110];
        data.cell.styles.fontStyle = 'bold';
      } else if (value === 'failed') {
        data.cell.styles.textColor = [185, 28, 28];
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text('Powered by roomnexa', margin, doc.internal.pageSize.getHeight() - 8);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 8, {
      align: 'right',
    });
  }

  doc.save(`${safeName}-migration-report.pdf`);
}

function isAcceptedFile(file) {
  if (!file?.name) return false;
  const lower = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export default function DataMigrationPage({ showHeader = true }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = isConfigurationMobileLayout();
  const fileInputRef = useRef(null);

  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [busyAction, setBusyAction] = useState(''); // 'validate' | 'import' | ''
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [successBanner, setSuccessBanner] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [validationFilter, setValidationFilter] = useState('all'); // all | valid | warnings | errors
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);
  const [actionBusyId, setActionBusyId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUploadedBy, setFilterUploadedBy] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [validationPage, setValidationPage] = useState(0);
  const [validationPerPage, setValidationPerPage] = useState(10);
  const [validationProgress, setValidationProgress] = useState({ validated: 0, total: 0 });
  const uploadSectionRef = useRef(null);
  const validateRequestIdRef = useRef(0);

  const textFieldSx = getConfigurationTextFieldSx(isDark);
  const neutralBtnSx = getConfigurationNeutralOutlinedButtonSx(isDark);
  const financeCardSx = getConfigurationFinanceCardSx(isDark, isMobile);
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 600,
    background: 'linear-gradient(90deg, #4ce1b6 0%, #2dd4a0 100%)',
    color: '#1a1a1a',
    boxShadow: 'none',
    '&:hover': {
      background: 'linear-gradient(90deg, #3dd1a8 0%, #22c997 100%)',
      boxShadow: '0 4px 14px rgba(76, 225, 182, 0.4)',
    },
    '&.Mui-disabled': {
      background: alpha(ACCENT, 0.35),
      color: alpha('#04120e', 0.55),
    },
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await fetchDataMigrationHistory();
      const rows = Array.isArray(data?.results) ? data.results.map(mapHistoryApiRow) : [];
      setHistoryRows(rows);
    } catch {
      // Keep existing rows if history API is unavailable
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const handleViewHistory = async (row) => {
    setViewRow(row);
    if (!row?.id || String(row.id).startsWith('local-')) return;
    try {
      const data = await fetchDataMigrationHistoryDetail(row.id);
      if (data?.result) {
        setViewRow(mapHistoryApiRow(data.result));
      }
    } catch {
      // Keep list row details if detail fetch fails
    }
  };

  const handleDownloadHistory = async (row) => {
    try {
      await downloadHistoryReport(row);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message || 'Could not download migration report.',
        severity: 'error',
      });
    }
  };

  const handleEditHistory = (row) => {
    setFileError('');
    setSuccessBanner(
      `Edit ready for “${row.fileName}”. Upload the updated file — validation starts automatically, then migrate from the footer.`
    );
    uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => fileInputRef.current?.click(), 250);
  };

  const handleDeleteHistory = async () => {
    if (!deleteRow?.id) return;
    const id = deleteRow.id;
    setActionBusyId(id);
    try {
      let deletedBookings = 0;
      let deletedPayments = 0;
      if (!String(id).startsWith('local-')) {
        const data = await deleteDataMigrationHistory(id);
        deletedBookings = Number(data?.deleted_bookings ?? 0);
        deletedPayments = Number(data?.deleted_payments ?? 0);
      }
      setHistoryRows((prev) => prev.filter((row) => String(row.id) !== String(id)));
      setDeleteRow(null);
      setFileError('');
      setSuccessBanner('');
      const parts = [];
      if (deletedBookings) parts.push(`${deletedBookings} booking(s)`);
      if (deletedPayments) parts.push(`${deletedPayments} payment(s)`);
      setSnackbar({
        open: true,
        message: parts.length
          ? `Deleted migration history and ${parts.join(', ')} for this hotel only.`
          : 'Deleted migration history.',
        severity: 'success',
      });
    } catch (err) {
      setFileError(err?.response?.data?.error || err?.message || 'Could not delete history entry.');
      setDeleteRow(null);
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || err?.message || 'Could not delete history entry.',
        severity: 'error',
      });
    } finally {
      setActionBusyId(null);
    }
  };

  const uploaderIds = useMemo(
    () => [...new Set(historyRows.map((row) => row.uploadedBy).filter(Boolean))],
    [historyRows]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return historyRows.filter((row) => {
      if (filterStatus && row.status !== filterStatus) return false;
      if (filterUploadedBy && row.uploadedBy !== filterUploadedBy) return false;
      if (filterDate) {
        if (migrationUploadedDateKey(row) !== filterDate) return false;
      }
      if (q) {
        const haystack = [row.fileName, row.fileFormat, row.uploadedBy, row.status]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [historyRows, search, filterStatus, filterUploadedBy, filterDate]);

  const pagedRows = useMemo(() => {
    const start = page * perPage;
    return filteredRows.slice(start, start + perPage);
  }, [filteredRows, page, perPage]);

  const validationRowsView = useMemo(() => {
    const rows = validationResult?.rows || [];
    if (validationFilter === 'valid') {
      return rows.filter((row) => row.valid && !(row.warnings || []).length);
    }
    if (validationFilter === 'warnings') {
      return rows.filter((row) => row.valid && (row.warnings || []).length > 0);
    }
    if (validationFilter === 'errors') {
      return rows.filter((row) => !row.valid);
    }
    return rows;
  }, [validationResult, validationFilter]);

  const pagedValidationRows = useMemo(() => {
    const start = validationPage * validationPerPage;
    return validationRowsView.slice(start, start + validationPerPage);
  }, [validationRowsView, validationPage, validationPerPage]);

  const canMigrate = Boolean(
    selectedFile
    && validationResult
    && Number(validationResult.valid_records || 0) > 0
    && busyAction !== 'validate'
  );

  useEffect(() => {
    setValidationPage(0);
  }, [validationFilter, validationResult]);

  const handleFilePick = (file) => {
    setFileError('');
    setValidationResult(null);
    setImportResult(null);
    setSuccessBanner('');
    setValidationFilter('all');
    setValidationPage(0);
    setValidationProgress({ validated: 0, total: 0 });
    if (!file) {
      validateRequestIdRef.current += 1;
      setSelectedFile(null);
      setBusyAction('');
      return;
    }
    if (!isAcceptedFile(file)) {
      validateRequestIdRef.current += 1;
      setSelectedFile(null);
      setBusyAction('');
      setFileError('Unsupported file type. Please upload a .csv, .xlsx, or .xls file.');
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      validateRequestIdRef.current += 1;
      setSelectedFile(null);
      setBusyAction('');
      setFileError(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }
    setSelectedFile(file);
    runValidation(file);
  };

  const onDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const file = event.dataTransfer?.files?.[0];
    handleFilePick(file);
  };

  const runValidation = async (file) => {
    if (!file) return;
    const requestId = ++validateRequestIdRef.current;
    setBusyAction('validate');
    setFileError('');
    setImportResult(null);
    setValidationResult(null);
    setValidationProgress({ validated: 0, total: 0 });
    try {
      const data = await validateDataMigrationFile(file, {
        onProgress: ({ validated, total }) => {
          if (requestId !== validateRequestIdRef.current) return;
          setValidationProgress({
            validated: Number(validated || 0),
            total: Number(total || 0),
          });
        },
      });
      if (requestId !== validateRequestIdRef.current) return;
      setValidationResult(data);
      setValidationFilter('all');
      setValidationProgress({
        validated: Number(data?.total_records || 0),
        total: Number(data?.total_records || 0),
      });
    } catch (err) {
      if (requestId !== validateRequestIdRef.current) return;
      const message = err?.response?.data?.error || err?.message || 'Validation failed.';
      setFileError(message);
      setValidationResult(null);
      setValidationProgress({ validated: 0, total: 0 });
    } finally {
      if (requestId === validateRequestIdRef.current) {
        setBusyAction((current) => (current === 'validate' ? '' : current));
      }
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    setConfirmImportOpen(false);
    setBusyAction('import');
    setFileError('');
    setSuccessBanner('');
    try {
      const data = await importDataMigrationFile(selectedFile);
      const failed = Number(data.failed_count || 0);
      const imported = Number(data.imported_count || 0);
      const statusLabel = imported > 0 ? 'Success' : 'Failed';
      const importedIds = (data.imported || []).map((row) => row.booking_id).filter(Boolean);
      markMigratedBookingsAsSeen(importedIds);

      setHistoryRows((prev) => {
        if (data.history) {
          const mapped = mapHistoryApiRow(data.history);
          return [mapped, ...prev.filter((row) => String(row.id) !== String(mapped.id))];
        }
        return [
          {
            id: `local-${Date.now()}`,
            fileName: data.file_name || selectedFile.name,
            fileFormat: fileFormatFromName(data.file_name || selectedFile.name),
            totalRecords: Number(data.total_records || 0),
            importedRecords: imported,
            failedRecords: failed,
            uploadedBy: 'You',
            uploadedAtIso: new Date().toISOString(),
            uploadedAt: formatNowStamp(),
            status: statusLabel,
          },
          ...prev,
        ];
      });
      if (!data.history) {
        loadHistory();
      }

      // Reset upload + validation UI after a finished run
      setSelectedFile(null);
      setValidationResult(null);
      setImportResult(null);
      setFileError('');
      setValidationFilter('all');
      setValidationPage(0);
      setValidationProgress({ validated: 0, total: 0 });
      setSuccessBanner('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (imported > 0) {
        setSnackbar({ open: true, message: 'Migrated successfully', severity: 'success' });
      } else {
        setSnackbar({
          open: true,
          message: data.message || 'Migration finished with no imported bookings.',
          severity: 'error',
        });
      }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Import failed.';
      setFileError(message);
      setSuccessBanner('');
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setBusyAction('');
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const clearFilters = () => {
    setSearch('');
    setFilterStatus('');
    setFilterUploadedBy('');
    setFilterDate('');
    setPage(0);
  };

  const columns = useMemo(
    () => [
      {
        field: 'fileName',
        label: 'File Name',
        minWidth: 180,
        render: (_value, row) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {row.fileName}
            </Typography>
          </Stack>
        ),
      },
      { field: 'fileFormat', label: 'File Format', minWidth: 90 },
      {
        field: 'totalRecords',
        label: 'Total Records',
        align: 'right',
        minWidth: 110,
        render: (value) => Number(value || 0).toLocaleString(),
      },
      {
        field: 'importedRecords',
        label: 'Imported',
        align: 'right',
        minWidth: 90,
        render: (value) => Number(value || 0).toLocaleString(),
      },
      {
        field: 'failedRecords',
        label: 'Failed',
        align: 'right',
        minWidth: 80,
        render: (value, row) => (
          <Typography
            variant="body2"
            sx={{
              fontWeight: row.failedRecords > 0 ? 700 : 500,
              color: row.failedRecords > 0 ? (isDark ? '#fca5a5' : '#b91c1c') : 'inherit',
            }}
          >
            {Number(value || 0).toLocaleString()}
          </Typography>
        ),
      },
      { field: 'uploadedBy', label: 'Uploaded By', minWidth: 120 },
      {
        field: 'uploadedAt',
        label: 'Uploaded Date & Time',
        minWidth: 180,
        render: (value, row) => formatMigrationDateTimeIst(row.uploadedAtIso || value),
      },
      {
        field: 'status',
        label: 'Migration Status',
        minWidth: 120,
        render: (value) => {
          const sx = statusChipSx(value, isDark);
          return (
            <Chip
              size="small"
              label={value}
              sx={{
                height: 24,
                fontWeight: 700,
                fontSize: '0.7rem',
                bgcolor: sx.bg,
                color: sx.color,
                border: `1px solid ${sx.border}`,
              }}
            />
          );
        },
      },
      {
        field: 'actions',
        label: 'Actions',
        align: 'center',
        minWidth: 110,
        sortable: false,
        render: (_value, row) => (
          <Stack direction="row" spacing={0.25} justifyContent="center">
            <Tooltip title="View details">
              <IconButton
                size="small"
                aria-label="View"
                onClick={() => handleViewHistory(row)}
                sx={{ color: ACCENT }}
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete history">
              <span>
                <IconButton
                  size="small"
                  aria-label="Delete"
                  disabled={actionBusyId === row.id}
                  onClick={() => setDeleteRow(row)}
                  sx={{ color: '#ef4444' }}
                >
                  {actionBusyId === row.id ? (
                    <CircularProgress size={14} />
                  ) : (
                    <DeleteOutlineIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [isDark, actionBusyId]
  );

  return (
    <Box sx={showHeader ? { p: 3, minHeight: '100vh', bgcolor: isDark ? '#12151c' : '#f2f4f7' } : getConfigurationEmbeddedRootSx(isMobile)}>
      {showHeader && (
        <Box sx={{ mb: 3 }}>
          <DashboardHeader
            title="Data Migration"
            navLinks={[
              { url: '/', label: 'Home' },
              { url: '/configuration/', label: 'Configuration' },
            ]}
          />
        </Box>
      )}

      <Grid container spacing={getConfigurationCardGridSpacing(isMobile)}>
        {successBanner ? (
          <Grid item xs={12}>
            <Fade in>
              <Alert
                severity="success"
                onClose={() => setSuccessBanner('')}
                sx={{
                  borderRadius: 2.5,
                  border: `1px solid ${alpha(ACCENT, 0.45)}`,
                  bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.1),
                  color: isDark ? '#d1fae5' : '#065f46',
                  '& .MuiAlert-icon': { color: ACCENT },
                  fontWeight: 600,
                  animation: `${driftIn} 0.35s ease`,
                }}
              >
                {successBanner}
              </Alert>
            </Fade>
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 3,
              px: { xs: 2, md: 2.75 },
              py: { xs: 2, md: 2.5 },
              border: `1px solid ${isDark ? alpha(ACCENT, 0.22) : alpha(ACCENT_DEEP, 0.14)}`,
              background: isDark
                ? `linear-gradient(135deg, ${alpha('#0b1220', 0.95)} 0%, ${alpha(ACCENT, 0.08)} 55%, ${alpha('#12202e', 0.9)} 100%)`
                : `linear-gradient(135deg, #f4fbf8 0%, #ffffff 42%, ${alpha(ACCENT, 0.16)} 100%)`,
              animation: `${driftIn} 0.4s ease`,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: 220,
                height: 220,
                borderRadius: '50%',
                right: -60,
                top: -80,
                background: `radial-gradient(circle, ${alpha(ACCENT, isDark ? 0.22 : 0.28)} 0%, transparent 70%)`,
                pointerEvents: 'none',
              }}
            />
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
              sx={{ position: 'relative' }}
            >
              <Box sx={{ minWidth: 0, maxWidth: 560 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <AutoAwesomeOutlinedIcon sx={{ color: ACCENT, fontSize: 20 }} />
                  <Typography
                    variant="overline"
                    sx={{
                      letterSpacing: '0.14em',
                      fontWeight: 800,
                      color: ACCENT_DEEP,
                      lineHeight: 1,
                    }}
                  >
                    Booking data migration
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.35rem', md: '1.6rem' },
                    letterSpacing: '-0.02em',
                    color: isDark ? '#f8fafc' : '#0f172a',
                    lineHeight: 1.2,
                    mb: 0.75,
                  }}
                >
                  Move past stays into RoomNexa cleanly
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#475569', lineHeight: 1.55 }}>
                  Download the template, upload your sheet, validate rows, then migrate. Imported bookings get
                  MG-YYYY-#### codes — scoped to this hotel only.
                </Typography>
              </Box>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                sx={{ flexShrink: 0 }}
              >
                {[
                  { n: '01', label: 'Template' },
                  { n: '02', label: 'Upload & validate' },
                  { n: '03', label: 'Migrate' },
                ].map((step, idx) => (
                  <Box
                    key={step.n}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.25,
                      py: 0.85,
                      borderRadius: 2,
                      bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#fff', 0.72),
                      border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha(ACCENT_DEEP, 0.12)}`,
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1.25,
                        display: 'grid',
                        placeItems: 'center',
                        fontWeight: 800,
                        fontSize: '0.72rem',
                        bgcolor: alpha(ACCENT, 0.2),
                        color: isDark ? ACCENT : ACCENT_DEEP,
                      }}
                    >
                      {step.n}
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: isDark ? '#e2e8f0' : '#0f172a' }}>
                      {step.label}
                    </Typography>
                    {idx < 2 ? (
                      <ArrowForwardRoundedIcon sx={{ fontSize: 16, color: alpha(ACCENT, 0.8), ml: 0.25 }} />
                    ) : null}
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              ...financeCardSx,
              '&::before': { display: 'none' },
              overflow: 'hidden',
              borderRadius: 3,
              border: `1px solid ${isDark ? alpha(ACCENT, 0.16) : alpha(ACCENT_DEEP, 0.1)}`,
              background: isDark
                ? `linear-gradient(180deg, ${alpha('#151922', 1)} 0%, ${alpha('#12151c', 1)} 100%)`
                : `linear-gradient(180deg, #ffffff 0%, ${alpha(ACCENT, 0.04)} 100%)`,
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    background: `linear-gradient(145deg, ${alpha(ACCENT, 0.28)} 0%, ${alpha(ACCENT, 0.08)} 100%)`,
                    border: `1px solid ${alpha(ACCENT, 0.35)}`,
                  }}
                >
                  <FileDownloadOutlinedIcon sx={{ color: ACCENT, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, color: isDark ? '#f1f5f9' : '#0f172a', letterSpacing: '-0.01em' }}>
                    Start with the official template
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                    Excel format · Guest, stay, room, bills, payments, status
                  </Typography>
                </Box>
              </Stack>
              <Button
                component="a"
                href={MIGRATION_TEMPLATE_URL}
                download={MIGRATION_TEMPLATE_FILENAME}
                variant="contained"
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{ ...primaryBtnSx, alignSelf: { xs: 'stretch', md: 'center' }, px: 2.25 }}
              >
                Download template
              </Button>
            </Stack>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {TEMPLATE_TIPS.map((tip) => (
                <Chip
                  key={tip}
                  size="small"
                  label={tip}
                  sx={{
                    height: 26,
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    borderRadius: 1.5,
                    bgcolor: isDark ? alpha('#fff', 0.04) : alpha(ACCENT_DEEP, 0.05),
                    border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha(ACCENT_DEEP, 0.12)}`,
                    color: isDark ? '#cbd5e1' : '#334155',
                  }}
                />
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper
            ref={uploadSectionRef}
            elevation={0}
            sx={{
              ...financeCardSx,
              '&::before': { display: 'none' },
              overflow: 'hidden',
              borderRadius: 3,
              border: `1px solid ${isDark ? alpha(ACCENT, 0.18) : alpha(ACCENT_DEEP, 0.12)}`,
              p: { xs: 2, md: 2.5 },
              position: 'relative',
              background: isDark
                ? `radial-gradient(1200px 420px at 0% 0%, ${alpha(ACCENT, 0.1)} 0%, transparent 55%), #151922`
                : `radial-gradient(1200px 420px at 100% 0%, ${alpha(ACCENT, 0.18)} 0%, transparent 50%), #ffffff`,
            }}
          >
            {(busyAction === 'validate' || busyAction === 'import') && (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  bgcolor: alpha(ACCENT, 0.15),
                  '& .MuiLinearProgress-bar': { bgcolor: ACCENT },
                }}
              />
            )}

            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  background: `linear-gradient(145deg, ${alpha(ACCENT, 0.3)} 0%, ${alpha(ACCENT, 0.08)} 100%)`,
                  border: `1px solid ${alpha(ACCENT, 0.4)}`,
                }}
              >
                <UploadFileIcon sx={{ color: ACCENT, fontSize: 26 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.15rem',
                    letterSpacing: '-0.015em',
                    color: isDark ? '#f8fafc' : '#0f172a',
                  }}
                >
                  Upload & validate
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                  Browse a file to upload and validate · CSV / XLSX / XLS · Max {MAX_FILE_SIZE_MB} MB
                </Typography>
              </Box>
            </Stack>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_ATTR}
              hidden
              onChange={(e) => {
                handleFilePick(e.target.files?.[0] || null);
                e.target.value = '';
              }}
            />

            <Box
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(false);
              }}
              onDrop={onDrop}
              sx={{
                width: '100%',
                borderRadius: 2.5,
                border: `1.5px ${selectedFile ? 'solid' : 'dashed'} ${
                  dragActive
                    ? ACCENT
                    : selectedFile
                      ? alpha(ACCENT, 0.35)
                      : isDark
                        ? alpha('#fff', 0.14)
                        : alpha('#0f172a', 0.12)
                }`,
                bgcolor: dragActive
                  ? alpha(ACCENT, isDark ? 0.12 : 0.08)
                  : isDark
                    ? alpha('#fff', 0.02)
                    : alpha(ACCENT_DEEP, 0.02),
                p: { xs: 1.75, md: 2.25 },
                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                animation: dragActive ? `${softPulse} 1.4s ease infinite` : 'none',
              }}
            >
              {!selectedFile ? (
                <Box
                  sx={{
                    width: '100%',
                    minHeight: { xs: 280, md: 320 },
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    py: { xs: 3.5, md: 5 },
                    px: { xs: 2, md: 4 },
                    animation: `${driftIn} 0.35s ease`,
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      background: isDark
                        ? `radial-gradient(ellipse 70% 55% at 50% 20%, ${alpha(ACCENT, 0.14)} 0%, transparent 70%)`
                        : `radial-gradient(ellipse 70% 55% at 50% 15%, ${alpha(ACCENT, 0.2)} 0%, transparent 68%)`,
                    }}
                  />

                  <Box
                    sx={{
                      position: 'relative',
                      width: 76,
                      height: 76,
                      mb: 2,
                      borderRadius: '22px',
                      display: 'grid',
                      placeItems: 'center',
                      background: `linear-gradient(155deg, ${alpha(ACCENT, 0.42)} 0%, ${alpha(ACCENT, 0.08)} 100%)`,
                      border: `1px solid ${alpha(ACCENT, 0.45)}`,
                      boxShadow: `0 12px 28px ${alpha(ACCENT, isDark ? 0.12 : 0.18)}`,
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 34, color: isDark ? ACCENT : ACCENT_DEEP }} />
                  </Box>

                  <Typography
                    sx={{
                      position: 'relative',
                      fontWeight: 800,
                      fontSize: { xs: '1.25rem', md: '1.45rem' },
                      letterSpacing: '-0.025em',
                      color: isDark ? '#f8fafc' : '#0f172a',
                      mb: 0.75,
                      maxWidth: 520,
                    }}
                  >
                    Drop your booking sheet to begin
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      position: 'relative',
                      color: isDark ? '#94a3b8' : '#64748b',
                      lineHeight: 1.55,
                      maxWidth: 460,
                      mb: 2.5,
                    }}
                  >
                    We check the file first, then validate each stay in place. When rows are ready, Migrate
                    appears below.
                  </Typography>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    justifyContent="center"
                    sx={{ position: 'relative', mb: 2.75, width: '100%', maxWidth: 560 }}
                  >
                    {[
                      { step: '1', label: 'Upload sheet' },
                      { step: '2', label: 'Auto-validate rows' },
                      { step: '3', label: 'Migrate bookings' },
                    ].map((item) => (
                      <Box
                        key={item.step}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.85,
                          px: 1.25,
                          py: 0.7,
                          borderRadius: 1.75,
                          bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#fff', 0.72),
                          border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha(ACCENT_DEEP, 0.12)}`,
                        }}
                      >
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: 1,
                            display: 'grid',
                            placeItems: 'center',
                            fontSize: '0.68rem',
                            fontWeight: 800,
                            bgcolor: alpha(ACCENT, 0.2),
                            color: isDark ? ACCENT : ACCENT_DEEP,
                          }}
                        >
                          {item.step}
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: isDark ? '#e2e8f0' : '#334155' }}>
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      ...primaryBtnSx,
                      position: 'relative',
                      px: 3,
                      py: 1.2,
                      fontSize: '0.95rem',
                    }}
                  >
                    Browse files
                  </Button>
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'relative',
                      mt: 1.5,
                      color: isDark ? '#64748b' : '#94a3b8',
                      fontWeight: 600,
                    }}
                  >
                    CSV, XLSX, or XLS · up to {MAX_FILE_SIZE_MB} MB
                  </Typography>
                  {fileError ? (
                    <Typography
                      variant="caption"
                      sx={{ position: 'relative', display: 'block', color: '#ef4444', fontWeight: 600, mt: 1.25 }}
                    >
                      {fileError}
                    </Typography>
                  ) : null}
                </Box>
              ) : (
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: isDark ? alpha('#000', 0.22) : alpha('#fff', 0.92),
                      border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.08)}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: alpha(ACCENT, 0.16),
                        border: `1px solid ${alpha(ACCENT, 0.35)}`,
                      }}
                    >
                      <InsertDriveFileOutlinedIcon sx={{ color: ACCENT, fontSize: 24 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                        {busyAction === 'validate' ? (
                          <CircularProgress size={14} sx={{ color: ACCENT }} />
                        ) : fileError ? (
                          <ErrorOutlineIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                        ) : validationResult ? (
                          <CheckCircleOutlineIcon sx={{ fontSize: 16, color: ACCENT }} />
                        ) : (
                          <CloudUploadIcon sx={{ fontSize: 16, color: ACCENT }} />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: fileError && busyAction !== 'validate' ? '#ef4444' : ACCENT,
                          }}
                        >
                          {busyAction === 'validate'
                            ? 'Validating…'
                            : fileError
                              ? 'Basic check failed'
                              : validationResult
                                ? 'Validated'
                                : 'Uploaded'}
                        </Typography>
                      </Stack>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: isDark ? '#f1f5f9' : '#0f172a',
                          fontSize: '0.9rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={selectedFile.name}
                      >
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                        {fileFormatFromName(selectedFile.name)} · {formatBytes(selectedFile.size)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={Boolean(busyAction)}
                        sx={{ ...neutralBtnSx, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                      >
                        Browse
                      </Button>
                      <IconButton
                        size="small"
                        aria-label="Remove file"
                        disabled={Boolean(busyAction)}
                        onClick={() => handleFilePick(null)}
                        sx={{
                          color: isDark ? '#94a3b8' : '#64748b',
                          border: `1px solid ${isDark ? alpha('#fff', 0.14) : alpha('#0f172a', 0.12)}`,
                          borderRadius: 2,
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>

                  {fileError ? (
                    <Typography variant="caption" sx={{ display: 'block', color: '#ef4444', fontWeight: 600 }}>
                      {fileError}
                    </Typography>
                  ) : null}

                  {fileError && !validationResult && busyAction !== 'validate' ? (
                    <Box
                      sx={{
                        width: '100%',
                        minHeight: 180,
                        borderRadius: 2,
                        bgcolor: alpha('#ef4444', isDark ? 0.1 : 0.05),
                        border: `1px solid ${alpha('#ef4444', 0.35)}`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        px: 2,
                        py: 3,
                      }}
                    >
                      <ErrorOutlineIcon sx={{ fontSize: 32, color: '#ef4444', mb: 1 }} />
                      <Typography sx={{ fontWeight: 800, color: isDark ? '#fecaca' : '#991b1b', mb: 0.5 }}>
                        Could not start full validation
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#fca5a5' : '#b91c1c', maxWidth: 480 }}>
                        Fix the file (use the official template) and browse again. Row-level validation only runs after
                        the sheet passes this basic check.
                      </Typography>
                    </Box>
                  ) : null}

                  {!validationResult && !fileError ? (
                    <Box
                      sx={{
                        width: '100%',
                        minHeight: 200,
                        borderRadius: 2,
                        bgcolor: isDark ? alpha('#000', 0.14) : alpha('#f8fafc', 0.95),
                        border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.06)}`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        px: 2,
                        py: 3,
                      }}
                    >
                      <CircularProgress size={28} sx={{ color: ACCENT, mb: 1.5 }} />
                      <Typography sx={{ fontWeight: 800, color: isDark ? '#e2e8f0' : '#0f172a', mb: 0.5 }}>
                        Validating your sheet…
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '1.35rem',
                          color: ACCENT,
                          mb: 0.75,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {Number(validationProgress.validated || 0).toLocaleString()}
                        {' / '}
                        {Number(validationProgress.total || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', maxWidth: 420, mb: 1.5 }}>
                        Bookings validated so far. Checking dates, rooms, amounts, and status.
                      </Typography>
                      <Box sx={{ width: '100%', maxWidth: 360 }}>
                        <LinearProgress
                          variant={
                            Number(validationProgress.total || 0) > 0 ? 'determinate' : 'indeterminate'
                          }
                          value={
                            Number(validationProgress.total || 0) > 0
                              ? Math.min(
                                  100,
                                  (Number(validationProgress.validated || 0)
                                    / Number(validationProgress.total || 1))
                                    * 100
                                )
                              : 0
                          }
                          sx={{
                            height: 8,
                            borderRadius: 999,
                            bgcolor: alpha(ACCENT, 0.15),
                            '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 999 },
                          }}
                        />
                      </Box>
                    </Box>
                  ) : null}

                  {validationResult ? (
                    <Fade in>
                      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: '0.95rem',
                            color: isDark ? '#f1f5f9' : '#0f172a',
                          }}
                        >
                          Validation results
                        </Typography>

                        <Grid container spacing={1}>
                          {[
                            {
                              label: 'Total',
                              value: validationResult.total_records || 0,
                              color: isDark ? '#e2e8f0' : '#0f172a',
                              bg: isDark ? alpha('#fff', 0.04) : alpha('#0f172a', 0.03),
                              border: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.06),
                              icon: <InsertDriveFileOutlinedIcon sx={{ fontSize: 16 }} />,
                            },
                            {
                              label: 'Valid',
                              value: validationResult.valid_records || 0,
                              color: ACCENT,
                              bg: alpha(ACCENT, isDark ? 0.14 : 0.1),
                              border: alpha(ACCENT, 0.45),
                              icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
                            },
                            {
                              label: 'Warnings',
                              value: validationResult.warning_count || 0,
                              color: '#fbbf24',
                              bg: alpha('#f59e0b', isDark ? 0.14 : 0.1),
                              border: alpha('#f59e0b', 0.45),
                              icon: <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />,
                            },
                            {
                              label: 'Invalid',
                              value: validationResult.invalid_records || 0,
                              color: '#f87171',
                              bg: alpha('#ef4444', isDark ? 0.14 : 0.08),
                              border: alpha('#ef4444', 0.45),
                              icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
                            },
                          ].map((stat) => (
                            <Grid item xs={6} sm={3} key={stat.label}>
                              <Box
                                sx={{
                                  py: 1.1,
                                  px: 1.25,
                                  borderRadius: 2,
                                  bgcolor: stat.bg,
                                  border: `1px solid ${stat.border}`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ color: stat.color, display: 'flex' }}>{stat.icon}</Box>
                                <Box>
                                  <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: stat.color, lineHeight: 1.1 }}>
                                    {Number(stat.value).toLocaleString()}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: stat.color,
                                      fontWeight: 700,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.04em',
                                      fontSize: '0.65rem',
                                    }}
                                  >
                                    {stat.label}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>

                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                          {[
                            { key: 'all', label: `All (${(validationResult.rows || []).length})`, accent: ACCENT },
                            {
                              key: 'valid',
                              label: `Ready (${(validationResult.rows || []).filter((r) => r.valid && !(r.warnings || []).length).length})`,
                              accent: ACCENT,
                            },
                            {
                              key: 'warnings',
                              label: `Warnings (${(validationResult.rows || []).filter((r) => r.valid && (r.warnings || []).length).length})`,
                              accent: '#f59e0b',
                            },
                            {
                              key: 'errors',
                              label: `Errors (${(validationResult.rows || []).filter((r) => !r.valid).length})`,
                              accent: '#ef4444',
                            },
                          ].map((tab) => {
                            const active = validationFilter === tab.key;
                            return (
                              <Chip
                                key={tab.key}
                                size="small"
                                clickable
                                label={tab.label}
                                onClick={() => {
                                  setValidationFilter(tab.key);
                                  setValidationPage(0);
                                }}
                                sx={{
                                  fontWeight: 700,
                                  height: 28,
                                  borderRadius: 1.5,
                                  ...(active
                                    ? {
                                        bgcolor: tab.accent,
                                        color: tab.key === 'all' || tab.key === 'valid' ? '#04120e' : '#fff',
                                        border: `1px solid ${tab.accent}`,
                                      }
                                    : {
                                        bgcolor: 'transparent',
                                        border: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#0f172a', 0.1)}`,
                                        color: isDark ? '#cbd5e1' : '#334155',
                                      }),
                                }}
                              />
                            );
                          })}
                        </Stack>

                        <TableContainer
                          sx={{
                            ...getConfigurationTableContainerSx(isMobile),
                            width: '100%',
                            maxHeight: 380,
                            overflow: 'auto',
                            borderRadius: 2,
                            border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
                          }}
                        >
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                {['Row', 'Guest', 'Stay', 'Room', 'Status', 'Result', 'Messages'].map((label) => (
                                  <TableCell
                                    key={label}
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: '0.68rem',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.04em',
                                      color: isDark ? '#94a3b8' : '#64748b',
                                      bgcolor: isDark ? '#12151c' : '#f8fafc',
                                      whiteSpace: 'nowrap',
                                      borderBottom: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
                                      position: 'sticky',
                                      top: 0,
                                      zIndex: 3,
                                      py: 1.15,
                                    }}
                                  >
                                    {label}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {pagedValidationRows.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={7}>
                                    <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', py: 3, textAlign: 'center' }}>
                                      No rows in this filter.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                pagedValidationRows.map((row) => {
                                  const hasErrors = !row.valid;
                                  const hasWarnings = (row.warnings || []).length > 0;
                                  const resultLabel = hasErrors ? 'Error' : hasWarnings ? 'Warning' : 'Ready';
                                  const resultColor = hasErrors
                                    ? { bg: alpha('#ef4444', isDark ? 0.18 : 0.1), color: isDark ? '#fca5a5' : '#b91c1c', border: alpha('#ef4444', 0.45) }
                                    : hasWarnings
                                      ? { bg: alpha('#f59e0b', isDark ? 0.18 : 0.12), color: isDark ? '#fbbf24' : '#b45309', border: alpha('#f59e0b', 0.45) }
                                      : { bg: alpha(ACCENT, isDark ? 0.18 : 0.12), color: isDark ? '#8de5cd' : '#0f766e', border: alpha(ACCENT, 0.45) };
                                  const accentBar = hasErrors ? '#ef4444' : hasWarnings ? '#f59e0b' : ACCENT;
                                  const messages = [...(row.errors || []), ...(row.warnings || [])];
                                  return (
                                    <TableRow
                                      key={row.row_number}
                                      hover
                                      sx={{
                                        bgcolor: hasErrors
                                          ? alpha('#ef4444', isDark ? 0.05 : 0.025)
                                          : hasWarnings
                                            ? alpha('#f59e0b', isDark ? 0.05 : 0.02)
                                            : 'transparent',
                                        '& td:first-of-type': {
                                          borderLeft: `3px solid ${accentBar}`,
                                        },
                                      }}
                                    >
                                      <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', color: isDark ? '#f1f5f9' : '#0f172a' }}>
                                        {row.row_number}
                                      </TableCell>
                                      <TableCell sx={{ minWidth: 130 }}>
                                        <Typography sx={{ fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', fontSize: '0.85rem' }}>
                                          {row.guest_name || '—'}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: isDark ? '#cbd5e1' : '#334155', whiteSpace: 'nowrap' }}>
                                          {`${formatMigrationStayDate(row.check_in)} → ${formatMigrationStayDate(row.check_out)}`}
                                        </Typography>
                                      </TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem', color: isDark ? '#cbd5e1' : '#334155' }}>
                                        <Typography component="span" sx={{ fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>
                                          {row.room_no || '—'}
                                        </Typography>
                                        {row.room_type ? (
                                          <Typography component="span" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                            {` · ${row.room_type}`}
                                          </Typography>
                                        ) : null}
                                      </TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem', color: isDark ? '#cbd5e1' : '#334155', textTransform: 'capitalize' }}>
                                        {row.status || '—'}
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          size="small"
                                          icon={
                                            hasErrors ? (
                                              <ErrorOutlineIcon sx={{ fontSize: '14px !important' }} />
                                            ) : hasWarnings ? (
                                              <WarningAmberOutlinedIcon sx={{ fontSize: '14px !important' }} />
                                            ) : (
                                              <CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />
                                            )
                                          }
                                          label={resultLabel}
                                          sx={{
                                            height: 24,
                                            fontWeight: 700,
                                            fontSize: '0.7rem',
                                            bgcolor: resultColor.bg,
                                            color: resultColor.color,
                                            border: `1px solid ${resultColor.border}`,
                                            '& .MuiChip-icon': { color: 'inherit' },
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ minWidth: 220, maxWidth: 420 }}>
                                        {messages.length ? (
                                          <Stack spacing={0.4}>
                                            {messages.slice(0, 3).map((msg, idx) => {
                                              const isErrorMsg = hasErrors && idx < (row.errors || []).length;
                                              return (
                                                <Typography
                                                  key={`${row.row_number}-${idx}`}
                                                  variant="caption"
                                                  sx={{
                                                    display: 'block',
                                                    color: isErrorMsg
                                                      ? (isDark ? '#fca5a5' : '#b91c1c')
                                                      : (isDark ? '#fbbf24' : '#b45309'),
                                                    lineHeight: 1.35,
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {msg}
                                                </Typography>
                                              );
                                            })}
                                            {messages.length > 3 ? (
                                              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                                                +{messages.length - 3} more
                                              </Typography>
                                            ) : null}
                                          </Stack>
                                        ) : (
                                          <Typography variant="caption" sx={{ color: ACCENT, fontWeight: 700 }}>
                                            Ready to import
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {validationRowsView.length > 0 ? (
                          <TablePagination
                            component="div"
                            count={validationRowsView.length}
                            page={validationPage}
                            onPageChange={(_event, newPage) => setValidationPage(newPage)}
                            rowsPerPage={validationPerPage}
                            onRowsPerPageChange={(event) => {
                              setValidationPerPage(parseInt(event.target.value, 10));
                              setValidationPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelRowsPerPage="Rows"
                            sx={{
                              borderTop: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
                              color: isDark ? '#cbd5e1' : '#475569',
                              '.MuiTablePagination-toolbar': { minHeight: 44, pl: 0.5 },
                              '.MuiTablePagination-selectIcon': { color: isDark ? '#999' : '#666' },
                            }}
                          />
                        ) : null}
                      </Box>
                    </Fade>
                  ) : null}
                </Box>
              )}
            </Box>

            {canMigrate ? (
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, color: isDark ? '#f1f5f9' : '#0f172a' }}>
                    Ready to migrate
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                    {Number(validationResult.valid_records || 0).toLocaleString()} valid row
                    {Number(validationResult.valid_records || 0) === 1 ? '' : 's'} will import as MG-YYYY-#### bookings
                    {Number(validationResult.invalid_records || 0) > 0
                      ? ` · ${Number(validationResult.invalid_records || 0).toLocaleString()} invalid skipped`
                      : ''}
                    .
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  disabled={Boolean(busyAction)}
                  onClick={() => setConfirmImportOpen(true)}
                  startIcon={
                    busyAction === 'import' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CloudUploadIcon />
                    )
                  }
                  sx={{
                    ...primaryBtnSx,
                    py: 1.15,
                    px: 2.5,
                    whiteSpace: 'nowrap',
                    minWidth: { sm: 160 },
                  }}
                >
                  {busyAction === 'import' ? 'Importing…' : 'Migrate'}
                </Button>
              </Box>
            ) : null}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              ...financeCardSx,
              '&::before': { display: 'none' },
              borderRadius: 3,
              border: `1px solid ${isDark ? alpha(ACCENT, 0.14) : alpha(ACCENT_DEEP, 0.1)}`,
            }}
          >
            <Box sx={getConfigurationEmbeddedSectionHeaderSx(isMobile)}>
              <Box sx={getConfigurationEmbeddedSectionLeadSx(isMobile)}>
                <Box sx={getConfigurationEmbeddedIconBoxSx(ACCENT, isMobile)}>
                  <HistoryEduOutlinedIcon sx={{ fontSize: isMobile ? 26 : 32, color: ACCENT }} />
                </Box>
                <Box>
                  <Typography sx={getConfigurationEmbeddedSectionTitleSx(isMobile)}>
                    Migration History
                  </Typography>
                  <Typography sx={getConfigurationEmbeddedSectionSubtitleSx(isMobile)}>
                    Past imports for this hotel · MG-YYYY-#### booking codes
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={clearFilters}
                sx={{ ...neutralBtnSx, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                Clear filters
              </Button>
            </Box>

            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              }}
            >
              <Grid container spacing={1.25} alignItems="center">
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Status"
                    value={filterStatus}
                    onChange={(e) => {
                      setFilterStatus(e.target.value);
                      setPage(0);
                    }}
                    sx={textFieldSx}
                  >
                    <MenuItem value="">All</MenuItem>
                    {STATUS_OPTIONS.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Uploaded By"
                    value={filterUploadedBy}
                    onChange={(e) => {
                      setFilterUploadedBy(e.target.value);
                      setPage(0);
                    }}
                    sx={textFieldSx}
                  >
                    <MenuItem value="">All</MenuItem>
                    {uploaderIds.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormDatePicker
                    label="Date"
                    size="small"
                    value={filterDate ? moment(filterDate) : null}
                    onChange={(newValue) => {
                      setFilterDate(newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '');
                      setPage(0);
                    }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search file name, status…"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(0);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: isDark ? '#999' : '#666' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={textFieldSx}
                  />
                </Grid>
              </Grid>
            </Box>

            <Alert
              severity="info"
              sx={{
                mb: 2,
                borderRadius: 2,
                py: 0.75,
                bgcolor: alpha(ACCENT, isDark ? 0.08 : 0.06),
                border: `1px solid ${alpha(ACCENT, 0.28)}`,
                color: isDark ? '#cbd5e1' : '#334155',
                '& .MuiAlert-icon': { color: ACCENT },
              }}
            >
              Imports for this hotel are saved and shown below on all devices. Created bookings use
              MG-YYYY-#### codes and appear in Booking List.
            </Alert>

            <DataTable
              columns={columns}
              data={pagedRows}
              emptyMessage={
                historyLoading
                  ? 'Loading migration history…'
                  : 'No migration uploads yet. Validate and import a file to see history here.'
              }
              pagination={{
                page,
                perPage,
                totalRecords: filteredRows.length,
              }}
              onPageChange={setPage}
              onPerPageChange={(value) => {
                setPerPage(value);
                setPage(0);
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      <Drawer
        anchor="right"
        open={Boolean(viewRow)}
        onClose={() => setViewRow(null)}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, {
            width: BOOKING_DRAWER_WIDTH_SX,
            maxWidth: '100vw',
          }),
        }}
      >
        {viewRow ? (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                px: 3,
                pt: 2.5,
                pb: 2,
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                background: isDark
                  ? 'linear-gradient(180deg, rgba(76,225,182,0.08) 0%, transparent 100%)'
                  : 'linear-gradient(180deg, rgba(76,225,182,0.06) 0%, transparent 100%)',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(ACCENT, 0.16),
                    border: `1px solid ${alpha(ACCENT, 0.35)}`,
                  }}
                >
                  <InsertDriveFileOutlinedIcon sx={{ color: ACCENT, fontSize: 24 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.05rem',
                      color: isDark ? '#f8fafc' : '#0f172a',
                      lineHeight: 1.3,
                      wordBreak: 'break-word',
                    }}
                  >
                    {viewRow.fileName || 'Migration details'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', mt: 0.35 }}>
                    {viewRow.fileFormat || 'FILE'} · {viewRow.uploadedAt || '—'}
                  </Typography>
                </Box>
                {(() => {
                  const sx = statusChipSx(viewRow.status, isDark);
                  return (
                    <Chip
                      size="small"
                      label={viewRow.status}
                      sx={{
                        height: 26,
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        bgcolor: sx.bg,
                        color: sx.color,
                        border: `1px solid ${sx.border}`,
                        flexShrink: 0,
                      }}
                    />
                  );
                })()}
                <IconButton
                  aria-label="Close"
                  onClick={() => setViewRow(null)}
                  size="small"
                  sx={{ color: isDark ? '#94a3b8' : '#64748b', ml: 0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2.5 }}>
              <Grid container spacing={1.25} sx={{ mb: 2 }}>
                {[
                  {
                    label: 'Total',
                    value: viewRow.totalRecords,
                    color: isDark ? '#e2e8f0' : '#0f172a',
                    labelColor: isDark ? '#94a3b8' : '#64748b',
                    bg: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
                    border: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                  },
                  {
                    label: 'Imported',
                    value: viewRow.importedRecords,
                    color: ACCENT,
                    labelColor: ACCENT,
                    bg: alpha(ACCENT, isDark ? 0.14 : 0.1),
                    border: alpha(ACCENT, 0.45),
                  },
                  {
                    label: 'Failed',
                    value: viewRow.failedRecords,
                    color: '#f87171',
                    labelColor: '#f87171',
                    bg: alpha('#ef4444', isDark ? 0.14 : 0.08),
                    border: alpha('#ef4444', 0.45),
                  },
                ].map((stat) => (
                  <Grid item xs={4} key={stat.label}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 1.25,
                        px: 1,
                        borderRadius: 2,
                        bgcolor: stat.bg,
                        border: `1px solid ${stat.border}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '1.25rem',
                          color: stat.color,
                          lineHeight: 1.2,
                        }}
                      >
                        {Number(stat.value || 0).toLocaleString()}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: stat.labelColor,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                  overflow: 'hidden',
                  mb: 2,
                }}
              >
                {[
                  { label: 'Uploaded by', value: viewRow.uploadedBy || '—' },
                  { label: 'Uploaded at', value: viewRow.uploadedAt || '—' },
                  { label: 'File format', value: viewRow.fileFormat || '—' },
                ].map((item, idx, arr) => (
                  <Stack
                    key={item.label}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    sx={{
                      px: 1.75,
                      py: 1.15,
                      bgcolor: idx % 2 === 0
                        ? (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.02)')
                        : 'transparent',
                      borderBottom:
                        idx < arr.length - 1
                          ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`
                          : 'none',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600, flexShrink: 0 }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDark ? '#f1f5f9' : '#0f172a',
                        fontWeight: 600,
                        textAlign: 'right',
                        wordBreak: 'break-word',
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Stack>
                ))}
              </Box>

              {viewRow.message ? (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(ACCENT, isDark ? 0.08 : 0.06),
                    border: `1px solid ${alpha(ACCENT, 0.28)}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      color: ACCENT,
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    Summary
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#334155', lineHeight: 1.5 }}>
                    {viewRow.message}
                  </Typography>
                </Box>
              ) : null}

              {(() => {
                const importedRows = Array.isArray(viewRow.details?.imported)
                  ? viewRow.details.imported
                  : [];
                const idList = Array.isArray(viewRow.importedBookingIds)
                  ? viewRow.importedBookingIds
                  : [];
                if (!importedRows.length && !idList.length) return null;
                const rowsToShow = importedRows.length
                  ? importedRows
                  : idList.map((bookingId) => ({ booking_id: bookingId, code: '', guest_name: '' }));
                return (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.02)',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 700,
                        color: isDark ? '#94a3b8' : '#64748b',
                        mb: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      Migrated bookings ({rowsToShow.length})
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: isDark ? '#64748b' : '#94a3b8', mb: 1.25 }}
                    >
                      Delete uses these booking IDs only (this hotel, MG- codes). Other bookings are not affected.
                    </Typography>
                    <Stack spacing={0.75} sx={{ maxHeight: 260, overflow: 'auto' }}>
                      {rowsToShow.map((item, idx) => (
                        <Stack
                          key={`${item.booking_id || item.code || idx}-${idx}`}
                          direction="row"
                          spacing={1}
                          justifyContent="space-between"
                          alignItems="flex-start"
                          sx={{
                            px: 1,
                            py: 0.75,
                            borderRadius: 1,
                            bgcolor: idx % 2 === 0
                              ? (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.03)')
                              : 'transparent',
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: isDark ? '#f1f5f9' : '#0f172a', fontWeight: 600, wordBreak: 'break-word' }}
                            >
                              {item.guest_name || '—'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                              {item.code || '—'}
                              {item.row_number != null ? ` · Row ${item.row_number}` : ''}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: ACCENT,
                              fontWeight: 700,
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                              flexShrink: 0,
                            }}
                          >
                            ID {item.booking_id ?? '—'}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                );
              })()}

              {(viewRow.details?.failed || []).length ? (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha('#ef4444', isDark ? 0.1 : 0.06),
                    border: `1px solid ${alpha('#ef4444', 0.35)}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      color: isDark ? '#fca5a5' : '#b91c1c',
                      mb: 1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    Failed rows
                  </Typography>
                  <Stack spacing={0.75}>
                    {(viewRow.details.failed || []).slice(0, 8).map((item, idx) => (
                      <Typography
                        key={`${item.row_number}-${idx}`}
                        variant="body2"
                        sx={{ color: isDark ? '#fecaca' : '#7f1d1d', lineHeight: 1.4 }}
                      >
                        Row {item.row_number}: {item.guest_name || '—'} —{' '}
                        {(item.errors || []).join('; ') || 'Failed'}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </Box>

            <Box
              sx={{
                ...getBookingDrawerFooterSx(isDark),
                p: 1.5,
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setViewRow(null)}
                    sx={{
                      ...getBookingDrawerOutlinedButtonSx(isDark),
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      borderRadius: 1.5,
                      py: 1.1,
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<DownloadOutlinedIcon />}
                    onClick={() => handleDownloadHistory(viewRow)}
                    sx={{
                      ...getBookingDrawerPrimaryButtonSx(isDark),
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      borderRadius: 1.5,
                      py: 1.1,
                    }}
                  >
                    Download report
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        ) : null}
      </Drawer>

      <Dialog
        open={confirmImportOpen}
        onClose={() => {
          if (busyAction !== 'import') setConfirmImportOpen(false);
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: isDark ? '#151922' : '#fff',
            backgroundImage: 'none',
            borderRadius: 2.5,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: isDark ? '#f8fafc' : '#0f172a', pb: 1 }}>
          Migrate bookings?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: isDark ? '#cbd5e1' : '#475569', lineHeight: 1.55 }}>
            Import valid rows from “{selectedFile?.name}” as bookings with MG-YYYY-#### codes. They will appear in
            Booking List for this hotel only.
            {validationResult ? (
              <>
                {' '}
                Ready: {Number(validationResult.valid_records || 0)} of{' '}
                {Number(validationResult.total_records || 0)} row(s).
              </>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmImportOpen(false)}
            disabled={busyAction === 'import'}
            sx={{ ...neutralBtnSx, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={busyAction === 'import'}
            sx={primaryBtnSx}
          >
            {busyAction === 'import' ? 'Importing…' : 'Migrate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: isDark ? '#151922' : '#fff',
            backgroundImage: 'none',
            borderRadius: 2.5,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: isDark ? '#f8fafc' : '#0f172a' }}>
          Delete migration and bookings?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: isDark ? '#cbd5e1' : '#475569', lineHeight: 1.55 }}>
            This deletes the history entry and only the migrated bookings stored on this batch
            {deleteRow?.fileName ? ` (“${deleteRow.fileName}”)` : ''}.
            Other hotels and non-migrated bookings are not affected.
            {(() => {
              const ids = Array.isArray(deleteRow?.importedBookingIds)
                ? deleteRow.importedBookingIds
                : Array.isArray(deleteRow?.details?.imported_booking_ids)
                  ? deleteRow.details.imported_booking_ids
                  : [];
              if (ids.length) {
                return ` ${ids.length} booking ID(s) will be targeted for removal.`;
              }
              if (deleteRow?.importedRecords) {
                return ` About ${deleteRow.importedRecords} MG- booking(s) may be removed using stored codes.`;
              }
              return '';
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setDeleteRow(null)} sx={{ ...neutralBtnSx, textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={Boolean(actionBusyId)}
            onClick={handleDeleteHistory}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            {actionBusyId ? 'Deleting…' : 'Delete all'}
          </Button>
        </DialogActions>
      </Dialog>

      {typeof document !== 'undefined'
        ? createPortal(
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbar.severity}
                sx={{
                  width: '100%',
                  backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                  color: isDark ? '#fff' : '#212121',
                }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>,
            document.body
          )
        : null}
    </Box>
  );
}
